import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen, Clock, BarChart, IndianRupee, Lock, CheckCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

export default function TestSeriesDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchTestDetails();
  }, [id]);

  const fetchTestDetails = async () => {
    try {
      const response = await api.get(`/test-series/${id}`);
      setTest(response.data);
      
      if (user) {
        const accessResponse = await api.get(`/test-series/${id}/has-access`);
        setHasAccess(accessResponse.data.has_access);
      }
    } catch (error) {
      toast.error('Failed to load test details');
      navigate('/test-series');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }

    setPurchasing(true);
    
    try {
      const orderResponse = await api.post('/payment/create-order', {
        test_series_id: test.id,
        amount: test.price
      });

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        order_id: orderResponse.data.order_id,
        name: "JEE Test Series",
        description: test.title,
        handler: async (response) => {
          try {
            await api.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              test_series_id: test.id
            });
            
            toast.success('Payment successful! Test series unlocked.');
            setHasAccess(true);
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: "#2E5CFF"
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
      razorpay.on('payment.failed', function (response) {
        toast.error('Payment failed. Please try again.');
      });
      
    } catch (error) {
      toast.error('Failed to initiate payment');
    } finally {
      setPurchasing(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty?.toLowerCase()) {
      case 'easy': return 'text-success';
      case 'medium': return 'text-warning';
      case 'hard': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="test-series-detail-page">
      {/* Navbar */}
      <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
            alt="Pinaka Logo"
            className="h-10 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex gap-3 items-center">
            <ThemeToggle />
            {user ? (
              <Button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} data-testid="nav-login-btn">
                Login
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/test-series')} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Test Series
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {test.thumbnail_url && (
                <div className="relative rounded-lg overflow-hidden mb-6 border border-border">
                  <img src={test.thumbnail_url} alt={test.title} className="w-full h-80 object-cover" />
                  <div className="absolute top-4 right-4 bg-card/90 backdrop-blur-sm px-4 py-2 rounded-full border border-border">
                    <span className={`text-sm font-semibold ${getDifficultyColor(test.difficulty)}`}>
                      {test.difficulty}
                    </span>
                  </div>
                </div>
              )}

              <div className="text-xs text-accent font-semibold mb-2 uppercase tracking-wide">{test.category}</div>
              <h1 className="text-4xl font-outfit font-bold mb-4">{test.title}</h1>
              
              <div className="flex items-center gap-6 text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  <span>{test.questions_count} Questions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{test.duration} Minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart className="w-5 h-5" />
                  <span className={getDifficultyColor(test.difficulty)}>{test.difficulty}</span>
                </div>
              </div>

              <div className="prose prose-invert max-w-none">
                <h3 className="text-xl font-outfit font-semibold mb-3">About this test series</h3>
                <p className="text-muted-foreground leading-relaxed">{test.description}</p>
              </div>

              <div className="mt-8 bg-card border border-border rounded-lg p-6">
                <h3 className="text-xl font-outfit font-semibold mb-4">What's included</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Full-length mock test with {test.questions_count} questions</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Instant score report with detailed analysis</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Unlimited attempts to improve your score</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span>Timed test environment for real exam experience</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="sticky top-24"
            >
              <div className="glass rounded-lg p-6 border border-border">
                {/* Admin Preview Banner */}
                {user?.role === 'admin' && (
                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6 text-center">
                    <Eye className="w-6 h-6 text-accent mx-auto mb-2" />
                    <p className="text-accent font-semibold text-sm">Admin Preview Mode</p>
                    <p className="text-xs text-muted-foreground mt-1">You're viewing this as an admin</p>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 text-4xl font-bold font-mono mb-2">
                    <IndianRupee className="w-8 h-8" />
                    <span>{test.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">One-time purchase</p>
                </div>

                {/* Admin: Show back to admin button */}
                {user?.role === 'admin' ? (
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-accent hover:bg-accent/90 text-white font-semibold"
                      onClick={() => navigate('/admin')}
                      data-testid="back-to-admin-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Admin Panel
                    </Button>
                  </div>
                ) : hasAccess ? (
                  <div className="space-y-3">
                    <div className="bg-success/10 border border-success/20 rounded-lg p-4 text-center">
                      <CheckCircle className="w-8 h-8 text-success mx-auto mb-2" />
                      <p className="text-success font-semibold">You own this test series</p>
                    </div>
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90 text-white font-semibold shadow-glow"
                      onClick={() => navigate(`/test/${test.id}`)}
                      data-testid="start-test-btn"
                    >
                      Start Test Now
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90 text-white font-semibold shadow-glow"
                      onClick={handlePurchase}
                      disabled={purchasing}
                      data-testid="purchase-btn"
                    >
                      {purchasing ? (
                        'Processing...'
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Purchase Now
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      Secure payment via Razorpay
                    </p>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-border space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-semibold">{test.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Questions</span>
                    <span className="font-semibold">{test.questions_count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-semibold">{test.duration} min</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Difficulty</span>
                    <span className={`font-semibold ${getDifficultyColor(test.difficulty)}`}>{test.difficulty}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
